/**
 * Switcher di organizzazione per gli utenti multi-org (es. un super-admin che
 * appartiene a più org). Cambia l'org ATTIVA dell'account: aggiorna
 * `activeRole` (= org_type, inviato come X-Active-Role → il BE risolve l'org di
 * quel tipo per il modulo) + agency, azzera `externalId` e la sede attiva, e
 * invalida le query così workspace e dati si ricalcolano dinamicamente.
 *
 * Compare SOLO se l'utente ha più di un'org (da /me/profile memberships);
 * altrimenti non renderizza nulla. Montato nella chrome principale
 * (CornerChrome) e nei moduli herika-admin (Pricing/Anagrafiche). Stile
 * allineato al BrandSwitcher (pill + menu a tendina).
 *
 * Nota: la risoluzione org del BE è per (utente, org_type, modulo); questo
 * switcher distingue org di TIPO diverso (es. OTA vs DMC). Per due org dello
 * STESSO tipo servirebbe far disambiguare il middleware via un header org
 * dedicato (X-Active-Role da solo non basta).
 */
import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Building2, Check } from "lucide-react";

import { useGetMyProfileApiV1MeProfileGet } from "@/api/generated/me/me";
import { useAppDispatch, useAppSelector } from "@/store";
import { selectProfile } from "@/store/selectors";
import { setActiveRole, setExternalId, setProfile } from "@/store/slices/profileSlice";
import { setActiveWorkspaceId } from "@/api/workspace-scope";
import { cn } from "@/lib/utils";

interface OrgOpt {
  org_id: string;
  org_name: string;
  org_type: string;
}

interface MembershipLike {
  org_id: string;
  org_name: string;
  org_type: string;
  status: string;
}

export function OrgSwitcher() {
  const dispatch = useAppDispatch();
  const profile = useAppSelector(selectProfile);
  const queryClient = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const { data } = useGetMyProfileApiV1MeProfileGet({
    query: { staleTime: 5 * 60_000 },
  });

  const orgs = React.useMemo<OrgOpt[]>(() => {
    const ms =
      (data as { memberships?: MembershipLike[] } | undefined)?.memberships ?? [];
    const map = new Map<string, OrgOpt>();
    for (const m of ms) {
      if (m.status === "ACTIVE" && !map.has(m.org_id)) {
        map.set(m.org_id, { org_id: m.org_id, org_name: m.org_name, org_type: m.org_type });
      }
    }
    return Array.from(map.values());
  }, [data]);

  React.useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (orgs.length <= 1) return null; // singola org → nessuno switcher

  const currentId = profile.agencyId ?? orgs[0].org_id;
  const currentOrg = orgs.find((o) => o.org_id === currentId) ?? orgs[0];

  function selectOrg(orgId: string) {
    setOpen(false);
    const o = orgs.find((x) => x.org_id === orgId);
    if (!o || o.org_id === profile.agencyId) return;
    dispatch(setActiveRole(o.org_type as Parameters<typeof setActiveRole>[0]));
    // `X-External-Id` è dedicato al booking engine, dove vale un
    // external_account_id (sede/EA) — NON un org_id: passargli l'org_id
    // faceva rispondere 400 a TUTTI i suoi endpoint (search, hotels, offers,
    // booking, payment, /me/*…). Lo switch org di pricing/directory avviene
    // via `X-Active-Role` (setActiveRole sopra), che quei middleware leggono;
    // `X-External-Id` lo ignorano. Qui azzeriamo l'external_account attivo del
    // booking: senza header il BE auto-risolve l'EA dal token per la nuova org.
    dispatch(setExternalId(undefined));
    dispatch(setProfile({ agencyId: o.org_id, agencyName: o.org_name }));
    setActiveWorkspaceId(null); // la sede attiva non ha senso nella nuova org
    void queryClient.invalidateQueries(); // workspace + dati si ricalcolano
  }

  return (
    <div ref={ref} className="pointer-events-auto relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-full px-1.5 py-1 text-foreground transition-opacity hover:opacity-70"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Cambia organizzazione"
      >
        <Building2 className="h-3.5 w-3.5 text-foreground/55" strokeWidth={1.6} />
        <span className="max-w-[150px] truncate font-heading text-[10.5px] font-medium uppercase tracking-[0.24em]">
          {currentOrg.org_name}
        </span>
        <svg
          className={cn("h-2.5 w-2.5 opacity-50 transition-transform", open && "rotate-180")}
          viewBox="0 0 10 6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M1 1l4 4 4-4" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 top-full z-40 mt-2 w-[260px] overflow-hidden rounded-[16px] bg-white/95 backdrop-blur-2xl shadow-[0_1px_0_rgba(255,255,255,0.7)_inset,0_18px_50px_-14px_rgba(15,15,15,0.18)] animate-riseIn">
          <div className="px-4 py-2.5 text-[9px] uppercase tracking-[0.22em] text-foreground/45">
            Organizzazione
          </div>
          {orgs.map((o) => {
            const active = o.org_id === currentId;
            return (
              <button
                key={o.org_id}
                type="button"
                onClick={() => selectOrg(o.org_id)}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-foreground/[0.03]"
              >
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[10px] bg-foreground/[0.04] text-foreground/70">
                  <Building2 className="h-[15px] w-[15px]" strokeWidth={1.5} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-[12.5px] font-medium tracking-tight text-foreground">
                      {o.org_name}
                    </span>
                    {active && (
                      <Check className="ml-auto h-3 w-3 shrink-0 text-foreground/60" strokeWidth={2} />
                    )}
                  </div>
                  <p className="mt-0.5 text-[11px] uppercase leading-snug tracking-wide text-foreground/45">
                    {o.org_type}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
